import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Platform } from 'react-native';
import { isWeb } from '@helpers';

interface EventLog {
  timestamp: string;
  eventType: string;
  data: any;
  hub?: string;
  connectionId?: string;
}

interface ConnectionStatus {
  appHub: {
    id: string | null;
    isConnected: boolean;
    isReconnecting: boolean;
    lastPing?: string;
  };
  anonymHub: {
    id: string | null;
    isConnected: boolean;
    isReconnecting: boolean;
    lastPing?: string;
  };
}

interface Statistics {
  totalEvents: number;
  eventsPerMinute: number;
  uniqueEventTypes: number;
  lastEventAge: string;
  mostFrequentEvents: Array<{ eventType: string; count: number }>;
}

const useSignalRDebugger = () => {
  const [unreadCount, setUnreadCount] = useState(0);
  const [lastViewedTimestamp, setLastViewedTimestamp] = useState<string | null>(null);
  const previousLogsLength = useRef(0);

  // Redux selectors
  const eventLogs = useSelector((state: any) => {
    const logs = state.logs?.eventsHistory || [];
    return logs;
  });
  const signalRState = useSelector((state: any) => state.signalR);

  const { appHubID, signalRAnonymID, appHubReconnecting, signalRAnonymReconnecting, appHubInit, signalRAnonymInit } = signalRState;

  // Process event logs with additional metadata
  const processedLogs = useMemo(() => {
    return eventLogs.map((log: EventLog) => {
      // Extract hub and connection info from event data if available
      let hub = null;
      let connectionId = null;

      if (log.data && typeof log.data === 'object') {
        hub = log.data.hub || null;
        connectionId = log.data.connectionId || null;
      }

      // Try to determine hub from event type
      if (!hub && log.eventType) {
        if (log.eventType.includes('SignalR:')) {
          const parts = log.eventType.split(':');
          if (parts.length > 2) {
            hub = parts[1];
          }
        }
      }

      return {
        ...log,
        hub,
        connectionId,
      };
    });
  }, [eventLogs]);

  // Track unread events
  useEffect(() => {
    if (processedLogs.length > previousLogsLength.current) {
      const newEvents = processedLogs.length - previousLogsLength.current;
      setUnreadCount((prev) => prev + newEvents);
    }
    previousLogsLength.current = processedLogs.length;
  }, [processedLogs]);

  // Calculate statistics
  const statistics: Statistics = useMemo(() => {
    if (processedLogs.length === 0) {
      return {
        totalEvents: 0,
        eventsPerMinute: 0,
        uniqueEventTypes: 0,
        lastEventAge: 'N/A',
        mostFrequentEvents: [],
      };
    }

    // Total events
    const totalEvents = processedLogs.length;

    // Calculate events per minute
    const now = new Date();
    const oneMinuteAgo = new Date(now.getTime() - 60000);
    const recentEvents = processedLogs.filter((log: EventLog) => new Date(log.timestamp) > oneMinuteAgo);
    const eventsPerMinute = recentEvents.length;

    // Unique event types
    const eventTypes = new Set(processedLogs.map((log: EventLog) => log.eventType));
    const uniqueEventTypes = eventTypes.size;

    // Last event age
    const lastEvent = processedLogs[0]; // Most recent is first
    let lastEventAge = 'N/A';
    if (lastEvent) {
      const age = now.getTime() - new Date(lastEvent.timestamp).getTime();
      if (age < 1000) {
        lastEventAge = 'Just now';
      } else if (age < 60000) {
        lastEventAge = `${Math.floor(age / 1000)}s ago`;
      } else if (age < 3600000) {
        lastEventAge = `${Math.floor(age / 60000)}m ago`;
      } else {
        lastEventAge = `${Math.floor(age / 3600000)}h ago`;
      }
    }

    // Most frequent events
    const eventCounts = new Map<string, number>();
    processedLogs.forEach((log: EventLog) => {
      const type = log.eventType.replace('SignalR:Event:', '');
      eventCounts.set(type, (eventCounts.get(type) || 0) + 1);
    });

    const mostFrequentEvents = Array.from(eventCounts.entries())
      .map(([eventType, count]) => ({ eventType, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);

    return {
      totalEvents,
      eventsPerMinute,
      uniqueEventTypes,
      lastEventAge,
      mostFrequentEvents,
    };
  }, [processedLogs]);

  // Connection status
  const connectionStatus: ConnectionStatus = useMemo(
    () => ({
      appHub: {
        id: appHubID,
        isConnected: !!appHubID && !appHubReconnecting,
        isReconnecting: appHubReconnecting,
        lastPing: undefined, // Could be tracked if we add ping events
      },
      anonymHub: {
        id: signalRAnonymID,
        isConnected: !!signalRAnonymID && !signalRAnonymReconnecting,
        isReconnecting: signalRAnonymReconnecting,
        lastPing: undefined,
      },
    }),
    [appHubID, signalRAnonymID, appHubReconnecting, signalRAnonymReconnecting]
  );

  // Clear unread count
  const clearUnread = useCallback(() => {
    setUnreadCount(0);
    if (processedLogs.length > 0) {
      setLastViewedTimestamp(processedLogs[0].timestamp);
    }
  }, [processedLogs]);

  // Export logs function
  const exportLogs = useCallback(() => {
    const exportData = {
      exportTime: new Date().toISOString(),
      platform: Platform.OS,
      connectionStatus: {
        appHub: appHubID,
        anonymHub: signalRAnonymID,
      },
      statistics,
      events: processedLogs,
    };

    const jsonStr = JSON.stringify(exportData, null, 2);

    if (isWeb) {
      // Web download
      const blob = new Blob([jsonStr], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `signalr-logs-${Date.now()}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } else {
      // Mobile - could use file system or share
      // For now, just log to console
      console.log('Export data:', jsonStr);
      // Could integrate with React Native Share or File System
    }
  }, [processedLogs, appHubID, signalRAnonymID, statistics]);

  // Detect event loss (simplified version)
  const detectEventLoss = useCallback(() => {
    // This is a simplified detection
    // In a real implementation, you'd track sequence numbers
    const eventGaps: Array<{ from: string; to: string; gap: number }> = [];

    for (let i = 1; i < processedLogs.length; i++) {
      const current = new Date(processedLogs[i].timestamp);
      const previous = new Date(processedLogs[i - 1].timestamp);
      const gap = previous.getTime() - current.getTime();

      // If gap is more than 10 seconds, might indicate lost events
      if (gap > 10000) {
        eventGaps.push({
          from: processedLogs[i].timestamp,
          to: processedLogs[i - 1].timestamp,
          gap: Math.floor(gap / 1000),
        });
      }
    }

    return eventGaps;
  }, [processedLogs]);

  return {
    connectionStatus,
    eventLogs: processedLogs,
    statistics,
    unreadCount,
    clearUnread,
    exportLogs,
    detectEventLoss,
  };
};

export default useSignalRDebugger;
